class WishlistManager {
  constructor() {
    this.customerId = this.getCustomerId();
    this.wishlists = [];
    this.currentWishlistId = null;
    this.metafieldNamespace = 'custom';
    this.metafieldKey = 'wishlists';
    this.init();
  }

  async init() {
    if (!this.customerId) return;
    
    await this.loadWishlists();
    this.attachEventListeners();
    this.updateButtonStates();
  }

  getCustomerId() {
    if (typeof Shopify !== 'undefined' && Shopify.customer) {
      return Shopify.customer.id;
    }
    return null;
  }

  async loadWishlists() {
    try {
      // Load from localStorage as cache
      const cached = localStorage.getItem(`wishlist_${this.customerId}`);
      if (cached) {
        const data = JSON.parse(cached);
        this.wishlists = data.lists || [];
      }
      
      // Set default wishlist or create one
      if (this.wishlists.length === 0) {
        await this.createWishlist('My Wishlist');
      } else {
        this.currentWishlistId = this.wishlists[0].id;
      }
    } catch (error) {
      console.error('Error loading wishlists:', error);
      this.wishlists = [];
    }
  }

  async saveWishlists() {
    try {
      const data = { lists: this.wishlists };
      
      // Save to localStorage
      localStorage.setItem(`wishlist_${this.customerId}`, JSON.stringify(data));
      
      // Note: In a production environment, you would also sync this to Shopify customer metafields
      // This requires a backend API or Shopify Function
      
      return { success: true };
    } catch (error) {
      console.error('Error saving wishlists:', error);
      return { success: false, error };
    }
  }

  async createWishlist(title) {
    const newWishlist = {
      id: `wishlist_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      title: title,
      createdAt: new Date().toISOString(),
      products: []
    };
    
    this.wishlists.push(newWishlist);
    this.currentWishlistId = newWishlist.id;
    await this.saveWishlists();
    return newWishlist;
  }

  generateShareToken() {
    return Math.random().toString(36).substr(2, 15) + Math.random().toString(36).substr(2, 15);
  }

  getCurrentWishlist() {
    return this.wishlists.find(list => list.id === this.currentWishlistId);
  }

  isProductInWishlist(productId) {
    const currentList = this.getCurrentWishlist();
    if (!currentList) return false;
    return currentList.products.some(p => p.productId === productId.toString());
  }

  async addToWishlist(productData) {
    const currentList = this.getCurrentWishlist();
    if (!currentList) {
      await this.createWishlist('My Wishlist');
      return this.addToWishlist(productData);
    }

    // Check if already in wishlist
    if (this.isProductInWishlist(productData.productId)) {
      return { success: false, message: 'Already in wishlist' };
    }

    currentList.products.push({
      productId: productData.productId.toString(),
      variantId: productData.variantId?.toString() || null,
      productTitle: productData.productTitle,
      productHandle: productData.productHandle,
      productImage: productData.productImage,
      productPrice: productData.productPrice,
      addedAt: new Date().toISOString()
    });

    const result = await this.saveWishlists();
    this.updateButtonStates();
    return result;
  }

  async removeFromWishlist(productId) {
    const currentList = this.getCurrentWishlist();
    if (!currentList) return { success: false };

    currentList.products = currentList.products.filter(
      p => p.productId !== productId.toString()
    );

    const result = await this.saveWishlists();
    this.updateButtonStates();
    return result;
  }

  attachEventListeners() {
    document.addEventListener('click', async (e) => {
      const button = e.target.closest('.wishlist-button');
      if (!button || button.classList.contains('wishlist-button--login')) return;

      e.preventDefault();
      
      const productId = button.dataset.productId;
      const isInWishlist = this.isProductInWishlist(productId);

      if (isInWishlist) {
        await this.removeFromWishlist(productId);
        this.showNotification('Removed from wishlist');
      } else {
        const productData = {
          productId: button.dataset.productId,
          productTitle: button.dataset.productTitle,
          productHandle: button.dataset.productHandle,
          productImage: button.dataset.productImage,
          productPrice: button.dataset.productPrice
        };
        
        await this.addToWishlist(productData);
        this.showNotification('Added to wishlist!');
      }
    });
  }

  updateButtonStates() {
    document.querySelectorAll('.wishlist-button').forEach(button => {
      const productId = button.dataset.productId;
      if (this.isProductInWishlist(productId)) {
        button.classList.add('is-in-wishlist');
        button.setAttribute('aria-label', `Remove from wishlist`);
      } else {
        button.classList.remove('is-in-wishlist');
        button.setAttribute('aria-label', `Add to wishlist`);
      }
    });
  }

  showNotification(message) {
    const notification = document.createElement('div');
    notification.className = 'wishlist-notification';
    notification.textContent = message;
    notification.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      background: #000;
      color: #fff;
      padding: 1rem 1.5rem;
      border-radius: 4px;
      z-index: 10000;
      animation: slideIn 0.3s ease;
    `;
    
    document.body.appendChild(notification);
    setTimeout(() => {
      notification.style.animation = 'slideOut 0.3s ease';
      setTimeout(() => notification.remove(), 300);
    }, 2000);
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    window.wishlistManager = new WishlistManager();
  });
} else {
  window.wishlistManager = new WishlistManager();
}

// Add CSS animations
const style = document.createElement('style');
style.textContent = `
  @keyframes slideIn {
    from { transform: translateX(100%); opacity: 0; }
    to { transform: translateX(0); opacity: 1; }
  }
  @keyframes slideOut {
    from { transform: translateX(0); opacity: 1; }
    to { transform: translateX(100%); opacity: 0; }
  }
`;
document.head.appendChild(style);
